import { useQuery } from 'data-fetching-client';
import { useState, useCallback } from 'react';
import debounce from 'react-utils/debounce';
import styled from 'styled-components';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { GET_OBJECT_TYPE_BY_ID } from 'automation-ui-enrollment/queries/ObjectTypesQueries';
import { GET_ALL_OBJECT_PROPERTIES_BY_OBJECT } from 'automation-ui-enrollment/queries/ObjectPropertiesQueries';
import usePollForListData from 'automation-ui-enrollment/hooks/usePollForListData';
import PreviewEnrollTable from 'automation-ui-enrollment/components/enrollment/manual/PreviewEnrollTable';
import { hasRowConfiguration } from 'automation-ui-enrollment/components/enrollment/manual/ObjectTableMetadata';
import ILSListSizeLoader from 'automation-ui-enrollment/components/enrollment/manual/ILSListSizeLoader';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TABLE_WIDTH = 750;
const SEARCH_DEBOUNCE_MS = 250;
const SCROLL_DEBOUNCE_MS = 300;
const SEARCH_QUERY_INITIAL_COUNT = 50;
const SEARCH_QUERY_INITIAL_OFFSET = 0;
const StyledEnrollPanelContainer = styled.div.withConfig({
  displayName: "PreviewEnrollPanel__StyledEnrollPanelContainer",
  componentId: "kcg171-0"
})(["max-width:550px;margin:auto;"]);
const PreviewEnrollPanel = ({
  objectTypeId,
  objectIds,
  listId,
  scrollContainer
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [listSize, setListSize] = useState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchQuery = useCallback(debounce(setDebouncedSearchQuery, SEARCH_DEBOUNCE_MS), []);
  const handleSearchQueryChange = ({
    target: {
      value
    }
  }) => {
    setSearchQuery(value);
    debouncedSetSearchQuery(value);
  };
  const {
    loading: objectTypeLoading,
    data: objectTypeResponse,
    error: objectTypeError
  } = useQuery(GET_OBJECT_TYPE_BY_ID, {
    variables: {
      objectTypeId
    }
  });
  const objectTypeDefinition = objectTypeResponse === null || objectTypeResponse === void 0 ? void 0 : objectTypeResponse.enrollment_objectTypeById;
  const {
    data: byObjectData,
    loading: byObjectLoading,
    error: byObjectError,
    fetchMore: byObjectFetchMore
  } = useQuery(GET_ALL_OBJECT_PROPERTIES_BY_OBJECT, {
    variables: {
      objectTypeId,
      objectIds,
      count: SEARCH_QUERY_INITIAL_COUNT,
      offset: SEARCH_QUERY_INITIAL_OFFSET,
      query: debouncedSearchQuery
    },
    skip: !objectIds || objectIds.length === 0
  });
  const {
    pollingStatus,
    data: byListIdData,
    loading: byListIdLoading,
    error: byListIdError,
    fetchMore: byListIdFetchMore
  } = usePollForListData({
    listId,
    listSize,
    objectTypeId,
    searchQuery: debouncedSearchQuery
  });
  const allProperties = byListIdData && byListIdData.allPropertiesByListId || byObjectData && byObjectData.allPropertiesByObject;
  const error = byListIdError || byObjectError;
  const loading = byListIdLoading || byObjectLoading;
  const fetchMore = listId ? byListIdFetchMore : byObjectFetchMore;
  const debouncedFetchMore = debounce(fetchMore, SCROLL_DEBOUNCE_MS);
  if (objectTypeLoading && !hasRowConfiguration(objectTypeId)) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 150,
      layout: "centered"
    });
  }
  if (objectTypeError && !hasRowConfiguration(objectTypeId)) {
    return /*#__PURE__*/_jsx(UIErrorMessage, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.error"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.errorDetail"
      })
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(StyledEnrollPanelContainer, {
      children: /*#__PURE__*/_jsx(UIErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.error"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.redesign.enrollExistingPanel.enrollFromList.table.errorDetail"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        position: 'relative'
      },
      children: /*#__PURE__*/_jsx(UIAbstractProgress, {
        autoStart: loading,
        render: ({
          value,
          done
        }) => {
          return !done && /*#__PURE__*/_jsx(UINanoProgress, {
            value: loading ? value * 2 : 100,
            animateOnComplete: done || !loading
          });
        }
      })
    }), listId && /*#__PURE__*/_jsx(ILSListSizeLoader, {
      listId: listId,
      setListSize: setListSize,
      pollingStatus: pollingStatus
    }), (pollingStatus === 'COMPLETE' || !listId) && /*#__PURE__*/_jsx(PreviewEnrollTable, {
      allObjectProperties: allProperties,
      objectTypeId: objectTypeId,
      tableWidth: TABLE_WIDTH,
      selectable: false,
      onLoadMore: debouncedFetchMore,
      objectTypeDefinition: objectTypeDefinition,
      scrollContainer: scrollContainer,
      searchQuery: searchQuery,
      handleSearchQueryChange: handleSearchQueryChange
    })]
  });
};
export default PreviewEnrollPanel;